'use client';
import React from 'react';
import { CompactAdCards } from '../CTAStackSlider/CompactAdCards';
import { ProductBasedCTALayout, ProductBasedCTA } from './../CTAStackSlider/ProductBasedCTA';
import { StackedCards } from './../CTAStackSlider/StackedCards';
import { StackedCardHeader } from './../CTAStackSlider/StackedCardHeader';
import type { ProductBasedCTAProps } from './../CTAStackSlider/ProductBasedCTA';
import { Carousel } from '@benzinga/ui';
import Hooks from '@benzinga/hooks';
import { SplideStyle } from '@benzinga/ui';
import { appEnvironment, appName } from '@benzinga/utils';
import styles from './CallToActionStackedSliderBlock.module.scss';

const AdvertorialCards = React.lazy(() =>
  import('./../CTAStackSlider/AdvertorialCards').then(module => ({ default: module.AdvertorialCards })),
);

export interface CallToActionStackedSliderBlockProps {
  compact_mode?: boolean;
  display: ProductBasedCTALayout;
  groups?: ProductBasedCTAProps[];
  mobile_items?: number;
  social_proofing?: string;
  title?: string;
  limit_aol_items?: number;
  location?: string;
}

export const CallToActionStackedSliderBlock: React.FC<CallToActionStackedSliderBlockProps> = ({
  compact_mode = false,
  display,
  groups,
  limit_aol_items = -1,
  location = 'default',
  mobile_items = 1,
  social_proofing,
  title,
}) => {
  const isBzApp = appEnvironment().isApp(appName.bz);
  const defaultPerPage = isBzApp ? 2 : 3;
  const ref = React.useRef<HTMLDivElement>(null);
  const { width } = Hooks.useRefElementSize(ref);
  const perPage = width <= 500 ? mobile_items : width <= 720 ? 2 : defaultPerPage;

  if (!Array.isArray(groups) || groups?.length === 0) return;

  return (
    <div className={styles.wrapper} ref={ref}>
      {display !== 'aol' && <StackedCardHeader heading={title} social_proofing={social_proofing} />}
      {(() => {
        if (display === 'vertical') {
          return (
            <div className={`cta-stacked ${SplideStyle.splide_slidewrap}`}>
              <Carousel
                component={campaign => {
                  return (
                    <ProductBasedCTA
                      button_text={campaign.button_text}
                      description={campaign.description}
                      highlight_list={campaign?.highlight_list ?? []}
                      image_url={campaign?.image_url}
                      layout={display}
                      location={location}
                      review={campaign.review}
                      review_url={campaign.review_url}
                      title={campaign.title}
                      url={campaign.url}
                    />
                  );
                }}
                items={groups}
                options={{
                  autoWidth: false,
                  breakpoints: {
                    750: {
                      perPage: 1,
                    },
                    900: {
                      perPage: 2,
                    },
                  },
                  focus: groups && groups.length > 2 ? 'center' : 0,
                  perPage,
                }}
                showArrowsOnlyOnOverflow={true}
                showPagination={true}
              />
            </div>
          );
        } else if (display === 'aol') {
          return (
            <StackedCards
              compact_mode={compact_mode}
              groups={groups}
              heading={title}
              initialItems={limit_aol_items}
              layout={display}
              location={location}
              social_proofing={social_proofing}
            />
          );
        } else if (display === 'advertorial') {
          return (
            <React.Suspense fallback={null}>
              <AdvertorialCards groups={groups} initialItems={limit_aol_items} layout={display} location={location} />
            </React.Suspense>
          );
        } else if (display === 'compact_ads') {
          return <CompactAdCards groups={groups} layout={display} location={location} />;
        } else {
          return (
            <div className={`flex flex-col md:gap-3 gap-0`}>
              {groups &&
                groups.map((campaign, index) => {
                  return (
                    <div className={`basis-full`} key={index}>
                      <ProductBasedCTA
                        button_text={campaign.button_text}
                        description={campaign.description}
                        highlight_list={campaign?.highlight_list ?? []}
                        image_url={campaign?.image_url}
                        layout={display}
                        location={location}
                        review={campaign?.review ?? 0}
                        review_url={campaign?.review_url ?? ''}
                        title={campaign.title}
                        url={campaign.url}
                      />
                    </div>
                  );
                })}
            </div>
          );
        }
      })()}
    </div>
  );
};
